"use client";

import styles from "@/styles/cart/goods-select-item.module.scss";
import { Checkbox, Popover } from "react-vant";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { getCompressedImageUrl } from "@/utils/utils";
import type { CartProduct } from "@/types/viewCart";
import { Cross } from "@react-vant/icons";

interface CartGoodsSelectItemProps {
  product: CartProduct;
  loading?: boolean;
  onSelect?: (record: CartProduct, selected: boolean) => void;
  onDelete?: (record: CartProduct) => void;
  onQtyChange?: (record: CartProduct, quantity: number) => void;
  onExpressChange?: (record: CartProduct, upgradeExpress: boolean) => void;
}

const isValidPostage = (postage: unknown) => {
  if (postage === null || postage === undefined || postage === "") {
    return false;
  }

  const postageValue = Number(postage);
  return Number.isFinite(postageValue) && postageValue >= 0;
};

export default function CartGoodsSelectItem(props: CartGoodsSelectItemProps) {
  const [quantity, setQuantity] = useState(props.product.quantity || 1);
  const [hasChooseUpgradeExpress, setHasChooseUpgradeExpress] = useState(
    props.product.upgradeExpress === 1 || false
  );
  const hasPostageError =
    props.product.isAvailable === 1 &&
    !props.loading &&
    !isValidPostage(props.product.postage);
  const hasShippingDetails = Boolean(
    props.product.courierName ||
      props.product.eta ||
      props.product.shipsFrom ||
      props.product.handlingTime ||
      props.product.transitionTime
  );
  const hasEtaDetails = Boolean(
    props.product.handlingTime ||
      props.product.transitionTime ||
      props.product.eta
  );

  const handleQuantityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newQuantity = parseInt(e.target.value, 10);

    if (newQuantity === 0) {
      props.onDelete?.(props.product);
      return;
    }

    setQuantity(newQuantity);
    if (props.onQtyChange) {
      props.onQtyChange(props.product, newQuantity);
    }
  };

  return (
    <div
      className={`${styles["comp-goods-select-item"]} ${
        hasPostageError ? styles["postage-error"] : ""
      }`}
    >
      <div
        className={`${styles["goods-info"]} ${
          !props.product.isAvailable ? styles["disabled"] : ""
        }`}
      >
        {hasPostageError ? (
          <button
            type="button"
            className={styles["postage-error-icon"]}
            aria-label="Delete item"
            onClick={() => props.onDelete?.(props.product)}
          >
            <Cross aria-hidden="true" />
          </button>
        ) : (
          <Checkbox
            className={styles["goods-checkbox"]}
            shape="square"
            iconSize="14px"
            checked={
              props.product.appSelectStatus && props.product.isAvailable
                ? true
                : false
            }
            disabled={!props.product.isAvailable ? true : false}
            onChange={() => {
              if (props.onSelect) {
                props.onSelect(props.product, !props.product.appSelectStatus);
              }
            }}
          />
        )}

        <Link href={props.product.url} className={styles["goods-img-link"]}>
          <div className={styles["goods-img-box"]}>
            <Image
              className={`${styles["goods-img"]} ${
                !props.product.isAvailable ? styles["goods-img-disabled"] : ""
              }`}
              src={getCompressedImageUrl(props.product.defaultFullUrl, 254)}
              alt={props.product.productName}
              width={127}
              height={127}
            />
            {!props.product.isAvailable ? (
              <div className={styles["goods-disabled-text"]}>Unavailable</div>
            ) : null}
          </div>
        </Link>

        <div className={styles["goods-info-text"]}>
          <div className={styles["name"]}>
            <Link href={props.product.url}>{props.product.productName}</Link>
          </div>
          <div className={styles["postage"]}>
            Postage:&nbsp;
            {props.loading ? (
              <>loading</>
            ) : hasPostageError ? (
              <span style={{ color: "#e6415f" }}>Out of delivery area</span>
            ) : props.product.savePostage > 0 ? (
              <>
                <div>
                  <span className={styles["postage-price"]}>
                    ${props.product.postage.toFixed(2)}
                  </span>
                  <span className={styles["postage-cross-price"]}>
                    ${props.product.normalPostage.toFixed(2)}
                  </span>
                </div>
              </>
            ) : (
              <span style={{ color: "#e6415f" }}>
                {props.product.isAvailable !== 1
                  ? "-"
                  : `$${Number(props.product.postage).toFixed(2)}`}
              </span>
            )}
          </div>
          {props.product.savePostage > 0 ? (
            <div className={styles["postage-save-price"]}>
              Save: ${props.product.savePostage.toFixed(2)}
            </div>
          ) : null}
          <div className={styles["unit-price"]}>
            Unit Price:&nbsp;
            <span style={{ color: "#e6415f" }}>${props.product.price}</span>
          </div>
          {!props.loading &&
            props.product.isAvailable === 1 &&
            hasShippingDetails && (
              <div className={styles["shipping-details"]}>
                <p className={styles["shipping-row"]}>
                  Shipping Carrier:&nbsp;
                  <span className={styles["shipping-value"]}>
                    {props.product.courierName || "-"}
                  </span>
                </p>
                <p className={styles["shipping-row"]}>
                  ETA:&nbsp;
                  <span className={styles["shipping-value"]}>
                    {props.product.eta || "-"}
                  </span>
                  {hasEtaDetails && (
                    <Popover
                      placement="bottom-end"
                      reference={
                        <button
                          type="button"
                          className={styles["eta-detail-trigger"]}
                        >
                          Detail
                        </button>
                      }
                    >
                      <div className={styles["eta-detail-popover"]}>
                        <div className={styles["eta-detail-row"]}>
                          <strong>Handling Time:</strong>
                          <span>{props.product.handlingTime || "-"}</span>
                        </div>
                        <div className={styles["eta-detail-row"]}>
                          <strong>Transition Time:</strong>
                          <span>{props.product.transitionTime || "-"}</span>
                        </div>
                        <div className={styles["eta-detail-row"]}>
                          <strong>Total delivery time:</strong>
                          <span>{props.product.eta || "-"}</span>
                        </div>
                        <div className={styles["eta-detail-note"]}>
                          (Estimated delivery time based on customer locations)
                        </div>
                      </div>
                    </Popover>
                  )}
                </p>
                <p className={styles["shipping-row"]}>
                  Ships From:&nbsp;
                  <span className={styles["shipping-value"]}>
                    {props.product.shipsFrom || "-"}
                  </span>
                </p>
              </div>
            )}
          <div className={styles["goods-operation"]}>
            <select
              value={props.product.isAvailable ? quantity : 1}
              onChange={handleQuantityChange}
              className={styles["quantity-select"]}
              disabled={!props.product.isAvailable ? true : false}
            >
              {[
                <option key={0} value={0}>
                  0 (Delete)
                </option>,
                ...Array.from(
                  {
                    length:
                      props.product.inStock >= 100
                        ? 100
                        : props.product.inStock || 1,
                  },
                  (_, i) => i + 1
                ).map((num) => (
                  <option key={num} value={num}>
                    {num == 100 ? "100(max)" : num}
                  </option>
                )),
              ]}
            </select>
            
            <Image
              className={styles["delete-icon"]}
              alt="icon-delete"
              src={`${process.env.IMAGE_DOMAIN}/svg/icon-delete.svg`}
              onClick={() => {
                if (props.onDelete) {
                  props.onDelete(props.product);
                }
              }}
              width={22}
              height={22}
            />
          </div>
          <div className={styles["goods-upgrade-express"]}>
            {props.product.appSelectStatus == 1 &&
            props.product.inStock > 0 &&
            (hasChooseUpgradeExpress || props.product.isShowUpgrade == 1) &&
            props.product.inStock > 0 ? (
              <Checkbox
                checked={props.product.upgradeExpress == 1}
                onChange={(e) => {
                  if (e) setHasChooseUpgradeExpress(true);
                  if (props.onExpressChange) {
                    props.onExpressChange(props.product, e);
                  }
                }}
                shape="square"
                iconSize="14px"
                style={{ marginTop: "10px" }}
              >
                Upgrade to Express Post
              </Checkbox>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
